/* Derived from the verified native PDP community gallery; page-owned runtime. */
(() => {
    const tagName = 'illustrators-community';
    if (window.customElements.get(tagName)) return;

    class IllustratorsCommunity extends HTMLElement {
      constructor() {
        super();
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
        this.handleIntersection = this.handleIntersection.bind(this);
        this.handlePreloadIntersection = this.handlePreloadIntersection.bind(this);
        this.handleVisibility = this.handleVisibility.bind(this);
        this.handleMotionPreference = this.handleMotionPreference.bind(this);
      }

      connectedCallback() {
        this.cleanupRuntime();
        this.abortController = new AbortController();
        this.isNearViewport = true;
        this.requiresImagePreload = this.hasAttribute('data-preload-images');
        this.imagesReady = !this.requiresImagePreload;
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

        if (this.requiresImagePreload) this.dataset.imagesPending = '';

        document.addEventListener('shopify:block:select', this.handleBlockSelect, {
          signal: this.abortController.signal
        });
        document.addEventListener('shopify:block:deselect', this.handleBlockDeselect, {
          signal: this.abortController.signal
        });
        document.addEventListener('visibilitychange', this.handleVisibility, {
          signal: this.abortController.signal
        });

        if (typeof this.motionQuery.addEventListener === 'function') {
          this.motionQuery.addEventListener('change', this.handleMotionPreference);
        } else if (typeof this.motionQuery.addListener === 'function') {
          this.motionQuery.addListener(this.handleMotionPreference);
        }

        if ('IntersectionObserver' in window) {
          this.visibilityObserver = new IntersectionObserver(this.handleIntersection, {
            rootMargin: '200px 0px'
          });
          this.visibilityObserver.observe(this);

          if (this.requiresImagePreload) {
            this.preloadObserver = new IntersectionObserver(this.handlePreloadIntersection, {
              rootMargin: '600px 0px'
            });
            this.preloadObserver.observe(this);
          }
        } else if (this.requiresImagePreload) {
          this.preloadImages();
        }

        this.dataset.enhanced = '';
        this.updateRuntimeState();
      }

      disconnectedCallback() {
        this.cleanupRuntime();
      }

      cleanupRuntime() {
        this.abortController?.abort();
        this.abortController = null;
        this.visibilityObserver?.disconnect();
        this.visibilityObserver = null;
        this.preloadObserver?.disconnect();
        this.preloadObserver = null;
        this.preloadPromise = null;

        if (this.motionQuery) {
          if (typeof this.motionQuery.removeEventListener === 'function') {
            this.motionQuery.removeEventListener('change', this.handleMotionPreference);
          } else if (typeof this.motionQuery.removeListener === 'function') {
            this.motionQuery.removeListener(this.handleMotionPreference);
          }
        }
        this.motionQuery = null;
      }

      handleIntersection(entries) {
        this.isNearViewport = entries.some((entry) => entry.isIntersecting);
        this.updateRuntimeState();
      }

      handlePreloadIntersection(entries) {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.preloadObserver?.disconnect();
        this.preloadObserver = null;
        this.preloadImages();
      }

      preloadImages() {
        if (!this.requiresImagePreload || this.preloadPromise) return;

        const controller = this.abortController;
        const images = Array.from(this.querySelectorAll('.il-community__image'));
        const waitForImage = (image) => new Promise((resolve) => {
          const settle = () => resolve(image.naturalWidth > 0);

          if (image.complete) {
            settle();
            return;
          }

          image.addEventListener('load', settle, { once: true, signal: controller.signal });
          image.addEventListener('error', settle, { once: true, signal: controller.signal });
        });

        images.forEach((image) => {
          image.loading = 'eager';
        });

        this.preloadPromise = Promise.all(images.map(waitForImage)).then((results) => {
          if (!this.isConnected || controller !== this.abortController) return;

          if (results.every(Boolean)) {
            this.imagesReady = true;
            delete this.dataset.imagesPending;
            delete this.dataset.imageLoadFailed;
          } else {
            this.dataset.imageLoadFailed = '';
          }

          this.updateRuntimeState();
        });
      }

      handleVisibility() {
        this.updateRuntimeState();
      }

      handleMotionPreference() {
        this.updateRuntimeState();
      }

      updateRuntimeState() {
        const shouldPause = document.hidden
          || this.isNearViewport === false
          || this.imagesReady === false
          || this.motionQuery?.matches;
        if (shouldPause) this.dataset.runtimePaused = '';
        else delete this.dataset.runtimePaused;
      }

      handleBlockSelect(event) {
        if (this.contains(event.target)) this.dataset.editorPaused = '';
      }

      handleBlockDeselect(event) {
        if (this.contains(event.target)) delete this.dataset.editorPaused;
      }
    }

    window.customElements.define(tagName, IllustratorsCommunity);
  })();
